import { resolveColorRgb, contrastRatio, compositeOverBackdrop } from '@symbo.ls/scratch'

// Home-board poster palette — the geex/bellforge direction as the DEFAULT
// workspace look (Nika 2026-08-09: "restyle all builtin workspace apps /
// home page widgets… from bellforge workspace you have all design direction
// which we should make default workspace").
//
// One hue per widget, binary ink, ink-alpha secondary system — the bellforge
// rotation (workspace/BELLFORGE_REDESIGN.md) generalized to the shell's home
// widgets. App shortcuts and their content siblings share a family so the
// board reads as one set (multicolor lives ACROSS the set, never within one
// card — design-direction.md §6). `greeting` is deliberately absent: it is
// the board's neutral adaptive anchor (≤2 neutral screens per set).
//
// Posters are theme-invariant by design (verified pattern from bellforge:
// hue + binary ink render identically in dark and light shells); everything
// secondary derives from the ink via the colour grammar, never a gray.
//
// WHAT THIS FILE DOES NOT DO: pick what a visual drawn ON the card reads in.
// The {bg, ink} pair below is the PLATE contract — a surface and the ink that
// reads on it (HomeWidgetPlate, HomeWidgetAvatars) — and it reads against the
// CARD only by accident, because a plate colour is never measured against the
// card's own background. A dark "plate" family (analytics, a near-black hue)
// is illegible there in the dark theme: DESIGN-HOME-WIDGET-HUE-DARK-PLATE-
// INVISIBLE-1. The on-surface answer — `--home-widget-accent`, derived per
// theme against the design system's own card ground — lives in
// functions/homeWidgetFrame.js (`homeWidgetAccentOnSurfaceVars`), not here;
// every bar/dot/spark/stat a widget draws on its card reads that var, never
// `--home-widget-hue` directly.
//
// Module-widget hue fallback (tickets/brief-product-ui-direction.md "Module
// widget hue resolution" — widget EXTENSIBILITY contract, 2026-08-12): this
// MAP is a FIXED house catalog keyed by the shell's OWN widget/route ids — a
// third-party app-module's widget (apps/bi's 'bi-revenue', a future
// AI-created widget, …) is NEVER in it, by construction, forever. Before the
// second `fallbackHue` parameter existed, every such widget silently fell
// through to the neutral overlay/title pair — discarding whatever hue the
// module's OWN manifest declared for it (`spec.iconBg`, or the newer
// top-level `hue`) — so a 4-widget module like BI rendered as 4 identical
// grey tiles instead of the purple/blue/green/orange its manifest names.
// `homeWidgetHue(id, fallbackHue, designSystem)` now tries the curated MAP
// first (zero behaviour change for every house id/route), and when that
// misses, resolves `fallbackHue` — a bare hue-family name, e.g. 'purple' —
// by MEASURING it (tickets/fable.md, closing the hole left by workspace
// `737b47e6`'s hand-seeded `_HUE_INK` table): `designSystem` is the caller's
// processed scratch CONFIG (`el.context.designSystem` — see AppHueTile.js),
// threaded through as a plain parameter so this stays a pure, DOM-less,
// `el`-less function with no reach into a global singleton. `resolveColorRgb`
// (`@symbo.ls/scratch`, the same package's `getSafeInk` already uses it — see
// `system/ink.js`) resolves the hue's REAL rgb from that config — applying
// the `+N`/`-N` tone grammar itself — and `_measuredInk` below picks
// whichever of pure black/white clears MORE WCAG contrast against it. A hue
// name that is not a brand hue at all (unresolvable — no DOM/config can name
// it) still falls through to WHITE ink, the documented historical default
// for an undecided hue (the pre-refactor global fallback was literally
// `iconBg || 'blue'` background with a fixed white ink; see
// HomeModuleWidget.Header.IconTile in pages/main.js) — there being nothing to
// measure is the one case still left to a guess, and it is the ONLY case
// left to one. A caller with no `designSystem` to hand (e.g. the module-scope
// `homeWidgetHue(id)` calls in pages/main.js, which never pass a
// `fallbackHue` and therefore never reach this branch at all) gets the exact
// same historical-default behaviour, unchanged.
// ── THE POSTER CATALOG (tickets/fable.md HOME-POSTER-BRAND-1, 2026-08-19) ──
// ⚠️ SINCE 2026-09-02 THIS IS NO LONGER THE HOME BOARD'S DEFAULT TREATMENT.
// The board renders one neutral solid surface for every card; a family below
// is painted only where the USER picked it (context menu ▸ "Add color"). Read
// `posterFamilyEntry` / `homeWidgetSurfaceHue` at the bottom of this file
// before assuming a house id still paints its hue on the / page. The map is
// still live for the route-keyed LAUNCHER tiles.
// Every house id/route below resolves to a POSTER FAMILY, and every family
// is a pair of SEMANTIC TOKENS of the shell design system
// (`designSystem/color.js`, `poster<Family>` + `poster<Family>Ink`) — not a
// raw brand hue. Same rendered defaults (the tokens alias the exact hues
// this map used to name), one difference that matters: scratch emits each
// token as a CSS var (`--color-posterStudio`, `--color-posterStudioInk`), so
// a BRANDED WORKSPACE can supply its own poster pairs through the record
// channel the shell already has — `Workspace.settings.designSystem.vars`,
// written inline on `<html>` by `workspaceConfigs._applyDesignSystem` and
// reverted on switch — and the WHOLE identity follows (card, launcher tile,
// re-plated rows, hover wash, secondary-ink steps) with zero code and zero
// tenant literal here. A workspace without those vars renders the defaults.
//   record contract (plain CSS colours, supply the PAIR):
//     "--color-posterStudio": "<hue>", "--color-posterStudioInk": "<white|black>"
//
// Third column = the SECONDARY-INK alpha, MEASURED per family (the smallest
// alpha of the binary ink that clears ≥4.6:1 — the AA text floor plus a
// margin — composited over ITS OWN hue; `__tests__/homePosterTokens.test.js`
// re-derives every number from the real design system and fails if one
// drifts). The old blanket `.55` was a grammar floor nobody had measured:
// on a saturated hue it read at 1.9–3.5:1 (calendar red 1.92, chat 2.04,
// studio 2.88 …), i.e. every poster Subtitle and body caption was under AA.
// On a deep hue the step sits near the full ink (meet .94) — hierarchy
// below it is carried by size/weight, not alpha; on gray2 it stays at .46.
// `inkMeta` on a poster equals the same step: there is no AA headroom under
// it (the `.45` meta grammar belongs to the row families below).
//
// Binary-ink law unchanged (design-direction.md §6): white is the ink of a
// deep/saturated hue, black only of a pale one. Three families were DEEPENED
// with the `−N` grammar so white clears ≥5.0:1 at full opacity (the Meet
// `green-20` precedent, now `green-45`): calendar `red`→`red-40`, chat
// `grassgreen`→`grassgreen-35`, meet `green-20`→`green-45`. The alias lives
// in designSystem/color.js; this file names only the semantic token.
const POSTER_FAMILIES = {
  studio: ['posterStudio', 'posterStudioInk', '.81'],
  tickets: ['posterTickets', 'posterTicketsInk', '.67'],
  chat: ['posterChat', 'posterChatInk', '.90'],
  calendar: ['posterCalendar', 'posterCalendarInk', '.92'],
  reminders: ['posterReminders', 'posterRemindersInk', '.58'],
  meet: ['posterMeet', 'posterMeetInk', '.94'],
  broadcast: ['posterBroadcast', 'posterBroadcastInk', '.60'],
  documents: ['posterDocuments', 'posterDocumentsInk', '.64'],
  agents: ['posterAgents', 'posterAgentsInk', '.77'],
  team: ['posterTeam', 'posterTeamInk', '.59'],
  analytics: ['posterAnalytics', 'posterAnalyticsInk', '.46'],
  people: ['posterPeople', 'posterPeopleInk', '.89'],
  health: ['posterHealth', 'posterHealthInk', '.59'],
  files: ['posterFiles', 'posterFilesInk', '.62']
}

// Widget id / app route → poster family. App shortcuts and their content
// siblings share a family so the board reads as one set (multicolor lives
// ACROSS the set, never within one card — design-direction.md §6).
// `greeting` is deliberately absent: it is the board's neutral adaptive
// anchor (≤2 neutral screens per set). Route aliases keep the search
// dropdown springboard (SEARCH_APPS rows carry `route`) and any launcher on
// the SAME family as the home board — one source of truth.
const HOUSE_HUE_MAP = {
  'app-studio': 'studio',
  projects: 'studio',
  'app-tickets': 'tickets',
  received: 'tickets',
  given: 'tickets',
  'app-chat': 'chat',
  'app-calendar': 'calendar',
  events: 'calendar',
  'next-up': 'calendar',
  'app-reminders': 'reminders',
  'app-meet': 'meet',
  'app-broadcast': 'broadcast',
  broadcast: 'broadcast',
  'app-documents': 'documents',
  assets: 'documents',
  'app-agents': 'agents',
  agents: 'agents',
  youragents: 'agents',
  'app-team': 'team',
  teamtime: 'team',
  'app-analytics': 'analytics',
  analytics: 'analytics',
  activity: 'analytics',
  'app-people': 'people',
  'app-health': 'health',
  '/studio': 'studio',
  '/tasks': 'tickets',
  '/notes': 'documents',
  '/files': 'files',
  '/chat': 'chat',
  '/calendar': 'calendar',
  '/meet': 'meet',
  '/reminders': 'reminders',
  // History reads the activity log — the analytics family.
  '/history': 'analytics',
  '/announcements': 'broadcast',
  '/team-and-agents': 'agents',
  '/agents': 'agents',
  '/team-members': 'team',
  '/analytics': 'analytics',
  '/workspace-analytics': 'analytics',
  '/people': 'people',
  '/health': 'health',
  '/system-status': 'health',
  '/admin/system': 'health',
  '/admin': 'analytics',
  '/admin/module': 'analytics',
  '/admin/permissions': 'analytics',
  '/admin/teams': 'analytics',
  '/admin/usage': 'analytics'
}

// Bare hue-family name → the house's art-directed form of it. Applies ONLY to
// the manifest-fallback path (a house id/route already resolves through
// `HOUSE_HUE_MAP` directly), so a module declaring `hue: 'green'` lands on the
// exact same plate as the house's own `app-meet`, rather than on the raw
// brand hex that fails the ink floor. This is an ART-DIRECTION choice (one
// `green` identity board-wide, keeping §6's Meet `green` + `red` pairing) —
// not a contrast workaround, so it survives the move to measured ink below:
// `_measuredInk('green-45', …)` still independently clears the 3:1 floor with
// white (5.03:1), it just no longer needs to be TOLD that. (`green-20` until
// 2026-08-19; the house Meet plate deepened to `green-45` for the 4.5:1 TEXT
// floor — the raw module hue stays a RAW token on purpose: a module's
// manifest hue is the module's own brand, not a poster, so a workspace's
// poster-record override must not silently re-skin it.)
const _HUE_ALIAS = { green: 'green-45' }

// Measure the binary ink for a resolved bg token, replacing the hand-seeded
// `_HUE_INK` table `737b47e6` shipped (deleted here — see tickets/fable.md).
// No second hand-authored list, no enumeration ceiling: any brand hue this
// repo will ever declare, including one no one has typed into this file,
// resolves through the SAME live design-system lookup a rendered
// `background: bg` would use.
//
// design-direction.md §6's binary-ink law is CATEGORICAL, not "whichever
// wins more contrast": white is the default ink for a deep/saturated hue,
// black only for a pale/bright one that fails white outright. Picking
// "whichever of black/white measures higher contrast" is the WRONG rule
// here — black nearly always out-measures white against a mid-tone hue (its
// luminance floor is 0, white's is 1, so black usually has more headroom),
// which would silently flip every hue that already clears the floor with
// white — `green-20` (3.61:1 white, but 5.82:1 black) and `blue` (3.62:1
// white, but 5.80:1 black) both did exactly this on the first pass, which
// would have regressed `green`'s art-directed white-ink Meet identity and
// the pre-existing `blue` pairing even though NEITHER was failing anything.
// The correct measurement is a FLOOR check: white wins whenever it already
// clears 3:1 (matching every hue the house has ever shipped white ink for,
// with no table needed to say so); only a hue that genuinely fails white
// falls to black.
//
// `resolveColorRgb` (`@symbo.ls/scratch`, the same resolver `getSafeInk`
// already uses — system/ink.js) walks `designSystem.color`/`.gradient` by
// name, applying the `+N`/`-N` additive tone shift itself (mirrors
// `getColorShade`, `smbls/packages/scratch/src/utils/color.js`) — so
// `'pink+20'`/`'cerulean+30'`-style tokens resolve exactly like a rendered
// element's `background:` would, with no separate tone-parsing here.
//
// Returns `null` when the token can't be resolved at all (not a registered
// brand hue and not a parsable CSS color, or no `designSystem` was handed
// in) — there is nothing to measure, so the caller keeps its own documented
// fallback. `resolveColorRgb`'s own color-string fallback can throw when the
// runtime's DOM is present but incomplete (a test-harness stub with no
// `document.body`, see `__tests__/testDomShim.js`) rather than degrading to
// its own "can't resolve" return — the try/catch below normalizes that into
// the same `null` "can't measure" signal, so a DOM-completeness quirk in one
// runtime can never make this pure function throw where a real browser or a
// full DOM never would.
const _measuredInk = (bg, designSystem) => {
  if (!designSystem) return null
  let resolved
  try {
    resolved = resolveColorRgb(bg, designSystem)
  } catch (_) {
    return null
  }
  if (!resolved) return null
  const composited = compositeOverBackdrop(resolved.rgb, resolved.alpha)
  const onWhite = contrastRatio(composited, [255, 255, 255])
  // White is the default/preferred ink (design-direction.md §6) — keep it
  // whenever it already clears the 3:1 non-text floor. Only fall to black
  // when white genuinely fails, and even then only if black actually
  // measures better (it will, for anything that fails white this badly —
  // this is a defensive comparison, not the decision rule).
  if (onWhite >= 3) return 'white'
  const onBlack = contrastRatio(composited, [0, 0, 0])
  return onBlack >= onWhite ? 'black' : 'white'
}

// Shared {bg, ink, inkSoft, inkMeta, wash, fill} contract builder — the ONE
// place that derives the ink-alpha secondary system (§6: secondary text .55
// floor, meta .45, interactive wash .08, resting fill .12) from a resolved
// {bg, ink} pair, so `homeWidgetHue` and `rowHueRotation` can never drift on
// the derivation even though they resolve their base pair differently.
//
// `inkMetaOverride` (optional, 3rd positional) — DESIGN_LANGUAGE.md §8.5
// rule 6 ("never dim with opacity on top of an already-muted token — the
// composite silently halves the ratio; use the muted token at full
// opacity"). The blanket `ink + '.45'` formula assumes every `{bg, ink}`
// pair has enough full-opacity headroom to survive a 45%-alpha dilution
// toward `bg` — true for `homeWidgetHue`'s deep-hue/white-ink posters, but
// MEASURABLY FALSE for `rowHueRotation('pastel')`'s `purple+32` fill
// (composited bellforge measurement rgb(147,101,207): full-opacity black
// clears the WCAG floor at only 5.00:1 — the smallest headroom in the
// family, by design a saturated-not-actually-pale tint — so ANY alpha
// dilution below 1.0 drops it under 4.5:1; verified computationally against
// every alpha step from 0 to 1, none between 0.99 and 0 clears 4.5:1).
// Pass a concrete, independently-measured-safe value here instead of
// trusting the blanket formula when a family's bg tone doesn't have that
// margin — never guessed, always computed via `contrastRatio`/
// `compositeOverBackdrop` against the family's real composited fills.
// `inkSoftOverride` (optional, 4th positional) — the same idea one step up:
// the POSTER families pass their MEASURED secondary alpha (see
// `POSTER_FAMILIES` above) instead of the blanket `.55`. Row families leave
// it unset and keep the grammar default.
const _hueEntry = (bg, ink, inkMetaOverride, inkSoftOverride) => ({
  bg,
  ink,
  inkSoft: inkSoftOverride || ink + '.55',
  inkMeta: inkMetaOverride || ink + '.45',
  wash: ink + '.08',
  fill: ink + '.12'
})

// `id` — a widget id or app route, resolved against the curated house
// catalog (unchanged contract: every existing single-argument call keeps
// its exact behaviour).
// `fallbackHue` — OPTIONAL. A bare hue-family name (e.g. 'purple') a
// THIRD-PARTY module declared for this exact widget in its own manifest
// (`spec.iconBg`, or the newer top-level `hue` — see HomeModuleWidget in
// pages/main.js). Consulted ONLY when `id` misses the house catalog, which
// is always true for a module id/route — the house catalog is fixed and
// never contains one. Returns null when neither resolves, so callers keep
// their existing neutral-fallback behaviour (never a raw/invalid CSS value).
// `designSystem` — OPTIONAL. The caller's processed scratch CONFIG
// (`el.context.designSystem` from inside an `el.call('homeWidgetHue', …)`
// site — see AppHueTile.js), threaded through as a plain argument so
// `_measuredInk` can MEASURE `fallbackHue`'s real contrast instead of
// guessing. Omit it (every module-scope caller with no `el` in scope, e.g.
// pages/main.js's single-argument calls) and the function behaves exactly
// as it always has for that call shape.
export function homeWidgetHue(id, fallbackHue, designSystem) {
  const entry = posterFamilyEntry(HOUSE_HUE_MAP[id])
  if (entry) return entry
  if (typeof fallbackHue === 'string' && fallbackHue) {
    const bg = _HUE_ALIAS[fallbackHue] || fallbackHue
    return _hueEntry(bg, _measuredInk(bg, designSystem) || 'white')
  }
  return null
}

// ── THE SUGGESTED-COLOUR PALETTE (2026-09-02, Nika: "on / page widgets, make
// solid - a default color for all of them. but add in context menu 'Add
// color' option so users can customize widget colors + suggested colors") ──
//
// The 14 families above stopped being the board's DEFAULT treatment on that
// date: every home card now renders the neutral adaptive surface
// (`_widgetCardSurface` in pages/main.js — what `greeting` always showed),
// and a family is painted ONLY where the user picked one. `HOUSE_HUE_MAP`
// stays live for the LAUNCHER surfaces that are not home-board cards (the
// header search-dropdown springboard's route-keyed `AppHueTile` rows), so a
// launcher tile keeps its app identity while the board is quiet.
//
// The catalog is therefore now TWO things at once: the launcher's house
// identity map, and the board's SUGGESTED-COLOUR palette. Both read the same
// measured entries, so a hue edit still moves everything together and
// `__tests__/homePosterTokens.test.js` still re-derives every constant.
//
// `posterFamilyEntry` is the ONE way a user colour becomes a
// {bg, ink, inkSoft, inkMeta, wash, fill} contract — the exact entry a mapped
// id used to get — so a picked colour and a (legacy) house poster can never
// render differently.
export function posterFamilyEntry(familyKey) {
  const family = POSTER_FAMILIES[familyKey]
  if (!family) return null
  // bg + ink are the family's SEMANTIC tokens; the secondary step is the
  // measured alpha of THAT ink token (`posterStudioInk.81`), which resolves
  // at paint time off the same var a record override rewrites.
  const soft = family[1] + family[2]
  return _hueEntry(family[0], family[1], soft, soft)
}

// The palette's render ORDER — declaration order of `POSTER_FAMILIES`, which
// is the board's own hue rotation (studio → files). Derived, never a second
// hand-kept list: a family added above appears in the picker with no edit here.
export const POSTER_FAMILY_KEYS = Object.keys(POSTER_FAMILIES)

// Readable names for the picker rows. These name the COLOUR, not the app the
// family is historically associated with: the user is choosing a colour for a
// widget that may have nothing to do with Studio or Meet, so "Electric blue"
// is the honest label and "Studio" would be a category error. Used as the
// swatch's accessible name + tooltip (the swatch itself carries the colour).
export const POSTER_FAMILY_LABELS = {
  studio: 'Electric blue',
  tickets: 'Orange',
  chat: 'Moss green',
  calendar: 'Brick red',
  reminders: 'Yellow',
  meet: 'Forest green',
  broadcast: 'Pink',
  documents: 'Sky blue',
  agents: 'Purple',
  team: 'Cyan',
  analytics: 'Graphite',
  people: 'Brown',
  health: 'Lime',
  files: 'Azure'
}

// The palette rows the "Add color" picker renders, in board order, with the
// currently-picked one flagged. Exposed as a FUNCTION (registered in
// functions/index.js) rather than letting the component import the tables
// directly: a component that closes over a module-scope const reads it as
// undefined after `frank.toJSON` (FA201–204), and a palette that renders
// empty on the platform but full locally is exactly the class of bug that
// rule exists to stop. `el.call('homeWidgetColorOptions', …)` resolves
// through `context.functions` on every runtime.
export function homeWidgetColorOptions(root, widgetId) {
  // Unconditional map read — same FA515 reasoning as `homeWidgetSurfaceHue`.
  const map = root ? root._homeWidgetColors : null
  const picked = map && widgetId ? map[widgetId] : null
  return POSTER_FAMILY_KEYS.map((key) => ({
    key,
    label: POSTER_FAMILY_LABELS[key],
    bg: POSTER_FAMILIES[key][0],
    ink: POSTER_FAMILIES[key][1],
    active: picked === key
  }))
}

// The palette's "App color" row — an app-store / module widget's OWN manifest
// hue, offered as the first suggestion.
//
// This row is the whole reason demoting the manifest hue costs the user
// nothing (see `homeWidgetSurfaceHue`'s header). The hue stops PAINTING by
// default, so a workspace's board is uniformly neutral until someone chooses
// otherwise; it stays one click away for whoever wants the app's identity on
// the board. Nothing is discarded — a module author's declared colour is
// surfaced as a suggestion instead of imposed as a default.
//
// Returns null when this widget has no manifest hue (every static house
// widget, and any module that declares none), which is what hides the row.
// Also null when the declared hue does not resolve in the handed-in design
// system — offering a swatch that would paint nothing is worse than offering
// no swatch.
//
// `key` is the RAW manifest hue, so picking it stores that exact value and
// `homeWidgetSurfaceHue` resolves it through the identical `_rawHueEntry`
// path — one derivation, so the swatch cannot preview a colour the card then
// paints differently.
export function homeWidgetAppColorOption(root, widgetId, appHue, designSystem) {
  const entry = _rawHueEntry(appHue, designSystem)
  if (!entry) return null
  // Unconditional map read — same FA515 reasoning as the resolver.
  const map = root ? root._homeWidgetColors : null
  const picked = map && widgetId ? map[widgetId] : null
  return {
    key: appHue,
    label: 'workspace.widgetColor.appColor',
    bg: entry.bg,
    ink: entry.ink,
    active: picked === appHue
  }
}

// A RAW hue name (a module's manifest hue, as the user picked it) → the same
// {bg, ink, …} contract a family key produces. Shared by the resolver and by
// the picker's "App color" row so the swatch and the card it will paint can
// never disagree on either half of the pair.
//
// Returns null when the hue is genuinely unresolvable AND a design system was
// available to prove it. That distinction matters: `_measuredInk` returns null
// both for "not a colour" and for "no design system handed in", and treating
// the second as unresolvable would neutralise every correct hue at a call site
// that has no config to thread. So the no-config branch keeps the documented
// historical default (white ink), and only a MEASURED failure neutralises —
// which is what stops a typo'd manifest hue from minting an invalid
// `background` declaration that drops, leaving white text on a transparent
// card.
const _rawHueEntry = (hue, designSystem) => {
  if (typeof hue !== 'string' || !hue) return null
  const bg = _HUE_ALIAS[hue] || hue
  if (!designSystem) return _hueEntry(bg, 'white')
  const ink = _measuredInk(bg, designSystem)
  return ink ? _hueEntry(bg, ink) : null
}

// The HOME-BOARD surface resolver — the ONE lookup every home card, card
// chrome and poster-ink descendant goes through since the board's default
// went neutral.
//
// ── ONE RULE, NO EXCEPTIONS (2026-09-02, second pass) ────────────────────
// Nika, after the first pass shipped: "here pipeline still has color and set
// to no color also does not change it. fix it fundamentally for also all
// app-store related apps and widgets too".
//
// The first pass kept a module's MANIFEST hue as that widget's default, one
// step below the user's pick. That was a real defect, not a taste call, and
// it produced exactly two visible bugs on the same widget:
//   • Pipeline (an app-store module widget) still painted purple on a board
//     whose stated default is one neutral solid — so "a default color for all
//     of them" was false for precisely the widgets a workspace installs most.
//   • "Default (no color)" DELETED the user's entry and then fell through to
//     that same manifest hue, so the card repainted the colour the user had
//     just cleared. The control read as broken because it WAS inert.
//
// A default that some widgets opt out of is not a default. So the precedence
// is now two steps, and the second is unconditional:
//   1. `root._homeWidgetColors[id]` — the user's OWN pick for this exact
//      widget. A poster FAMILY KEY, or a RAW hue when the user picked the
//      module's own colour from the palette's "App color" row.
//   2. `null` — NEUTRAL. Every widget, every plane: static, app-store module,
//      AI-created. Callers keep their adaptive-neutral fallbacks (`title`,
//      `caption`, `hover`, `overlay`).
// There is no third step and no sentinel to unwind, which is what makes
// "Default (no color)" genuinely neutralise everything: clearing the entry
// leaves nothing behind it.
//
// THE MANIFEST HUE IS NOT LOST, it is DEMOTED from a default to a suggestion.
// `homeWidgetColorOptions` offers it as the first swatch ("App color"), so a
// module's identity is one click away for whoever wants it. Picking it stores
// the RAW hue — not a sentinel that re-reads the manifest — so a widget the
// user deliberately coloured cannot silently repaint when the module ships a
// new manifest.
//
// The LAUNCHER is deliberately untouched: `homeWidgetHue` still resolves the
// house catalog and a manifest hue for the route-keyed search-springboard
// tiles. Nika's ask names "/ page widgets", and a launcher row IS the app's
// identity in a menu rather than a board card the user arranges.
//
// `root` is the ROOT STATE (`s.root`), not an element — this stays a pure
// function with no DOM and no `el`, exactly like `homeWidgetHue`.
// ⚠️ The colour map is read UNCONDITIONALLY — never behind a short-circuit
// that a falsy `id` could skip (FA515). Every card, header, icon plate and
// body row on the board resolves its paint through this one call, and the
// auto-tracker subscribes to exactly the signals a run actually READS: a run
// that skipped the read would leave that element permanently unsubscribed
// from `_homeWidgetColors`, so the pick would persist and survive reload yet
// never repaint until a full reload. Costs one property read.
export function homeWidgetSurfaceHue(root, id, designSystem) {
  const map = root ? root._homeWidgetColors : null
  const picked = map && id ? map[id] : null
  if (typeof picked !== 'string' || !picked) return null
  return posterFamilyEntry(picked) || _rawHueEntry(picked, designSystem)
}

// Row-hue rotations — geex reference frames 11/22/23 (workspace/design-refs/
// geex, tickets/brief-product-ui-direction.md "two colour registers"). Unlike
// `homeWidgetHue` (ONE hue per whole poster/widget), these cycle a small
// family of hues PER ROW so a list reads as one rotating set instead of one
// flat color:
//   'muted'  — full-bleed inbox/broadcast rows: olive/green/orange/red,
//              deep enough for WHITE ink (frame 11).
//   'pastel' — tonal transaction/order rows: lavender/lemon/mint/sky tints,
//              pale enough for BLACK ink (frames 22/23).
// Same {bg, ink, inkSoft, inkMeta, wash, fill} contract as homeWidgetHue so
// row components never branch on which family they got.
// `pastel` 3rd-column `inkMetaOverride` — MEASURED, not the blanket `.45`
// formula (see `_hueEntry`'s docblock). Ground truth: live-composited
// bellforge Enterprise rgb values for these four exact fills — `purple+32`
// rgb(147,101,207), `yellow+20` rgb(252,224,106), `phosphorus+32`
// rgb(220,248,85), `cerulean+32` rgb(32,220,255) — run through this
// package's own `contrastRatio`/`compositeOverBackdrop` (identical math
// `_measuredInk` above uses), computed with a real safety margin (targeting
// >=5.0:1, not a razor-thin 4.50 that could round-trip fail under render
// jitter) rather than the largest technically-passing alpha:
//   purple+32:      full-opacity black is the CEILING at 5.00:1 — this fill
//                    is not actually pale (a saturated mid-purple despite
//                    the `+32` lighten), so it has zero headroom for ANY
//                    dilution; every alpha < 1.0 measured under 4.5:1.
//                    Override is the literal `'black'` (opaque, no `.NN`
//                    suffix) — NOT `null`/falsy: `_hueEntry`'s formula is
//                    `inkMetaOverride || ink + '.45'`, so a falsy override
//                    would silently fall through to the broken blanket
//                    formula this fix exists to stop using here.
//   yellow+20:      alpha .65 -> 6.29:1 (headroom to spare; .59 is the
//                    lowest-alpha floor for >=5.0, rounded up for margin)
//   phosphorus+32:  alpha .65 -> 6.54:1 (.58 floor)
//   cerulean+32:    alpha .65 -> 5.74:1 (.61 floor)
// Re-derive with scratchpad/probe_ink.mjs-style math (contrastRatio +
// compositeOverBackdrop from `@symbo.ls/scratch`) if this palette's tone
// values ever change — these are computed constants, not eyeballed.
const ROW_HUE_FAMILIES = {
  muted: [
    ['brown', 'white'],
    ['green-10', 'white'],
    ['orange-10', 'white'],
    ['red-10', 'white']
  ],
  pastel: [
    ['purple+32', 'black', 'black'],
    ['yellow+20', 'black', 'black.65'],
    ['phosphorus+32', 'black', 'black.65'],
    ['cerulean+32', 'black', 'black.65']
  ]
}

export function rowHueRotation(index, family) {
  const list = ROW_HUE_FAMILIES[family] || ROW_HUE_FAMILIES.muted
  const i = (((Number(index) || 0) % list.length) + list.length) % list.length
  const hit = list[i]
  return _hueEntry(hit[0], hit[1], hit[2])
}
